import { Link } from 'react-router-dom';
import { LegalDocument, PublicLayout, type LegalSection } from '../components/layout/PublicLayout';
import { publicPageSeo, useSeo } from '../hooks/useSeo';

const Bullets = ({ items }: { items: string[] }) => (
  <ul>
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const SECTIONS: LegalSection[] = [
  {
    id: 'collect',
    heading: '1. Information We Collect',
    body: (
      <>
        <p>We may collect information about you in a variety of ways:</p>
        <Bullets
          items={[
            'Personal Data: Name, email address, telephone number, and demographic information you voluntarily provide during registration.',
            'Derivative Data: IP address, browser type, operating system, access times, and pages viewed.',
            'Financial Data: Payment method details collected at checkout — stored by our payment processor, not us.',
            'Data From Social Networks: Profile information if you connect social accounts to our Services.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'use',
    heading: '2. Use of Your Information',
    body: (
      <>
        <p>We use your information to:</p>
        <Bullets
          items={[
            'Create and manage your account',
            'Process payments and fulfil subscription orders',
            'Send account-related emails and notifications',
            'Monitor usage to improve the Services',
            'Prevent fraudulent transactions and protect against criminal activity',
            'Respond to customer service requests and resolve disputes',
          ]}
        />
      </>
    ),
  },
  {
    id: 'disclose',
    heading: '3. Disclosure of Your Information',
    body: (
      <>
        <p>We may share information in the following circumstances:</p>
        <Bullets
          items={[
            'By Law or to Protect Rights: When required by legal process or to protect the safety of others.',
            'Third-Party Service Providers: Partners who assist in payment processing, hosting, analytics, and customer support.',
            'Business Transfers: In connection with a merger, acquisition, or sale of assets.',
            'Affiliates: Shared with affiliates bound by this Privacy Policy.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'security',
    heading: '4. Security of Your Information',
    body: (
      <p>
        We use administrative, technical, and physical security measures to protect your personal information. While we take
        reasonable precautions, no method of data transmission or storage is 100% secure.
      </p>
    ),
  },
  {
    id: 'rights',
    heading: '5. Your Rights',
    body: (
      <>
        <p>Subject to applicable data protection laws, you have the right to:</p>
        <Bullets
          items={[
            'Access, update, or delete the information we hold about you',
            'Object to the processing of your personal information',
            'Request restriction of processing',
            'Receive a portable copy of your data',
            'Withdraw consent at any time where processing is based on consent',
          ]}
        />
        <p>
          To exercise these rights, contact us at <a href="mailto:support@cmsh.io">support@cmsh.io</a>.
        </p>
      </>
    ),
  },
  {
    id: 'children',
    heading: '6. Policy for Children',
    body: (
      <p>
        We do not knowingly solicit or collect information from children under the age of 13. If you become aware of any data
        collected from a child under 13, please contact us immediately.
      </p>
    ),
  },
  {
    id: 'changes',
    heading: '7. Changes to This Privacy Policy',
    body: (
      <p>
        We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on
        this page and updating the "Last updated" date. Please review this policy periodically.
      </p>
    ),
  },
  {
    id: 'contact',
    heading: '8. Contact Us',
    body: (
      <>
        <p>
          Questions about this Privacy Policy? Contact us at <a href="mailto:support@cmsh.io">support@cmsh.io</a>.
        </p>
        <address className="mt-2 rounded-control border border-border bg-surface px-5 py-4 text-sm not-italic leading-relaxed text-muted">
          <strong>Codemina Ltd</strong>
          <br />
          Company No. 14528227
          <br />
          71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, UK
          <br />
          +44 7309 650106
        </address>
      </>
    ),
  },
];

export default function PrivacyPolicyPage() {
  useSeo(publicPageSeo('/privacy-policy'));
  return (
    <PublicLayout>
      <LegalDocument
        title="Privacy Policy"
        lastUpdated="February 21, 2026"
        intro={
          <>
            Your privacy is important to us. This Privacy Policy explains how <strong className="text-text">Codemina Ltd</strong>{' '}
            ("we," "us," or "our") collects, uses, discloses, and safeguards your information when you use our website and
            services.
          </>
        }
        sections={SECTIONS}
        related={{ label: 'Terms of Use', to: '/terms-of-use' }}
        footer={
          <p className="border-t border-border pt-6 text-center text-sm text-muted">
            Also review our{' '}
            <Link to="/terms-of-use" className="font-medium">
              Terms of Use
            </Link>
          </p>
        }
      />
    </PublicLayout>
  );
}
